import { Expression, ExpressionGroup, ExpressionGroupsState } from '../types';

// This is a simplified version of the backend's expected structure.
// We are focusing on transforming the `expressions` and `expressionsStructure`.
interface BackendCondition {
  expressions: Array<{
    fieldIdentifier: string;
    type: string;
    operation: string;
    value: any[];
  }>;
  expressionsStructure: number[][];
}

// Operators that we will expand from a single UI expression into multiple backend expressions.
const EXPANDABLE_OPERATORS = ['equals', 'not_equals'];

/**
 * A helper function to create a Cartesian product of scenarios. This is needed to correctly
 * distribute AND logic across nested OR conditions from multi-value 'equals' expressions.
 * @param scenarioBlocks - An array of scenario lists, e.g., [[[0]], [[1], [2]]]
 */
function getCartesianProduct(scenarioBlocks: number[][][]): number[][] {
  if (!scenarioBlocks || scenarioBlocks.length === 0) {
    return [];
  }

  return scenarioBlocks.reduce(
    (acc, currentBlock) => {
      if (acc.length === 0) return currentBlock; // Initial case
      if (currentBlock.length === 0) return acc; // Skip empty blocks

      return acc.flatMap(accScenario =>
        currentBlock.map(currentScenario => [...accScenario, ...currentScenario])
      );
    },
    [] as number[][]
  );
}

/**
 * Transforms the flexible frontend ExpressionGroupsState into the rigid
 * `(A AND B) OR (C AND D)` structure that the backend condition engine expects.
 *
 * @param {ExpressionGroupsState | null} state - The expression state from the UI.
 * @returns {BackendCondition | undefined} The transformed condition object or undefined if input is null.
 */
export function buildConditionDefinition(state: ExpressionGroupsState | null): BackendCondition | undefined {
  if (!state || !state.groups) {
    return undefined;
  }

  const allExpressions: BackendCondition['expressions'] = [];
  const allScenarios: BackendCondition['expressionsStructure'] = [];

  for (const group of Object.values(state.groups)) {
    const groupExpressions = Object.values(group.expressions)
      .filter(expr => expr.isCompleted && expr.field && expr.operator);
    
    if (groupExpressions.length === 0) {
      continue;
    }

    // This will hold the list of scenarios generated by each expression in the group.
    // For a single expression, it might be [[[0]]].
    // For a multi-value 'equals' expression, it might be [[[0]], [[1]]].
    // For a multi-value 'not_equals' expression, it might be [[[0, 1]]].
    const scenarioBlocks: number[][][] = [];
    
    // First, process each expression to generate its backend expressions and its own scenario logic.
    for (const expression of groupExpressions) {
      const startIndex = allExpressions.length;
      const { value, operator } = expression;
      const isExpandable = value && value.length > 1 && EXPANDABLE_OPERATORS.includes(operator!);

      if (isExpandable) {
        // --- Multi-Value Expression Expansion ---
        const expanded = value.map(singleValue => ({
          fieldIdentifier: expression.field!.identifier,
          type: expression.field!.fieldType,
          operation: expression.operator!,
          value: [singleValue],
        }));
        allExpressions.push(...expanded);
        
        const indices = Array.from({ length: expanded.length }, (_, i) => startIndex + i);

        if (operator === 'equals') {
          // 'ANY OF' (OR logic): each value gets its own scenario.
          scenarioBlocks.push(indices.map(index => [index]));
        } else { // not_equals
          // 'NONE OF' (AND logic): all values go into a single scenario.
          scenarioBlocks.push([indices]);
        }
      } else {
        // --- Single-Value Expression ---
        allExpressions.push({
          fieldIdentifier: expression.field!.identifier,
          type: expression.field!.fieldType,
          operation: expression.operator!,
          value: expression.value,
        });
        // A single expression is just a single scenario containing its own index.
        scenarioBlocks.push([[startIndex]]);
      }
    }

    // Now, combine the scenario blocks based on the group's operator.
    if (group.operator === 'AND') {
      // For an AND group, we must find the Cartesian product to distribute the logic correctly.
      const combinedScenarios = getCartesianProduct(scenarioBlocks);
      allScenarios.push(...combinedScenarios);
    } else { // group.operator is 'OR'
      // For an OR group, the logic is simple: just add all scenarios from all blocks.
      const combinedScenarios = scenarioBlocks.flat();
      allScenarios.push(...combinedScenarios);
    }
  }
  
  if (allExpressions.length === 0) {
    return undefined;
  }

  return {
    expressions: allExpressions,
    expressionsStructure: allScenarios,
  };
} 